import { Box, Container, Header } from '@amzn/awsui-components-core';
import { ContentType, ContentWorkflow } from '@amzn/awscat-pattern-library-service-model';
import React, { useState, type JSX } from 'react';
import constants from '../../../common/constants';
import LocalStorage from '../../../common/LocalStorage';
import ReviewChecklistControls from '../action-controls/ReviewChecklistControls';
import ChecklistGroup from '../../common/ChecklistGroup';
import ChecklistInfo from '../../../model/ChecklistInfo';
import {
  apgCodeGuidelinesLink,
  awsWellArchitectPrinciplesLink,
  contentSuggestionsLink,
  hasCodeLinks,
  isForInternalContentAudience,
  latestIconsLink,
  reviewerBrReviewGuideLink,
  reviewerChecklistListName,
} from './ContentUtils';

interface Props {
  onReviewComplete: () => void;
  workflow: ContentWorkflow;
  approvalDisabled?: boolean;
  disableApproveMessage?: string;
}

const ReviewerChecklist: React.FunctionComponent<Props> = (props: Props) => {
  const { onReviewComplete, workflow, approvalDisabled, disableApproveMessage } = props;
  const {
    contentId,
    draftWorkflowId: workflowId,
    draftContentTypeMetadata,
    draftContentMetadata,
    contentAudience,
  } = workflow;
  const [allChecked, setAllChecked] = useState<boolean>(false);

  const { contentType } = draftContentMetadata;
  const hasCode = hasCodeLinks(draftContentTypeMetadata);
  const isForInternalAudience = isForInternalContentAudience(contentAudience);

  const strategyItems: Array<string | JSX.Element> = [
    'The metadata (Configuration section) is correct.',
    'All links are accessible by the intended audience.',
    'Diagram source files (.pptx) are attached.',
    'The strategy describes the business use case (e.g., migration, modernization).',
    'The strategy identifies one or more customer business outcomes.',
    <>
      <span>
        The strategy is supported by guides and patterns in APG Library, or if applicable,{' '}
      </span>
      {contentSuggestionsLink}
      <span> have been submitted by the author.</span>
    </>,
    'The strategy is original, not a duplicate or possibly plagiarized.',
    'The strategy covers the right level of depth and detail for senior management and C-level executives.',
    'The resources section includes links that complement the strategy.',
    "The resources section does NOT include external links that promote a competitor's solution.",
    'The strategy is consistent with AWS Cloud strategy and service positioning.',
    'The strategy includes the latest AWS services and methodology guidance.',
    <>
      <span>The strategy adheres to the </span>
      {awsWellArchitectPrinciplesLink}
      <span>.</span>
    </>,
  ];

  const guideItems: Array<string | JSX.Element> = [
    hasCode ? (
      <>
        <span>All the included code adheres to </span>
        {apgCodeGuidelinesLink}
        <span>.</span>
      </>
    ) : (
      ''
    ),
    'The guide is original, not a duplicate or possibly plagiarized.',
    'All links are accessible by the intended audience.',
    'Diagram source files (.pptx) are attached.',
    'The guide describes the business use case (e.g., migration, modernization).',
    'The guide identifies one or more customer business outcomes.',
    <>
      <span>
        The guide is supported by a strategy and patterns in APG Library, or if applicable,{' '}
      </span>
      {contentSuggestionsLink}
      <span> have been submitted by the author.</span>
    </>,
    'The guide covers right level of depth and detail for technical architects and leads.',
    'The guide covers multiple options, including pros and cons of each option.',
    'The information in the guide is correct and up to date.',
    <>
      <span>The guide adheres to the </span>
      {awsWellArchitectPrinciplesLink}
      <span>.</span>
    </>,
  ];

  const patternItems: Array<string | JSX.Element> = [
    hasCode ? (
      <>
        <span>All the included code adheres to </span>
        {apgCodeGuidelinesLink}
        <span>.</span>
      </>
    ) : (
      ''
    ),
    'The pattern is original, not a duplicate or possibly plagiarized.',
    'All links are accessible by the intended audience.',
    'Diagram source files (.pptx) are attached.',
    <>
      <span>The pattern is supported by a guide in APG Library, or if applicable, </span>
      {contentSuggestionsLink}
      <span> have been submitted by the author.</span>
    </>,
    'The pattern covers the right level of depth and detail for developers or other implementers.',
    <>
      <span>The pattern has an accurate architecture diagram that uses the </span>
      {latestIconsLink}
      <span>.</span>
    </>,
    'The prerequisites and limitations section provides complete information.',
    'The epics section provides the instructions required to complete the pattern.',
    isForInternalAudience
      ? ''
      : 'The pattern contains no internal tools, links, or proprietary information.',
    'The information in the pattern is correct and up to date.',
    <>
      <span>The pattern adheres to the </span>
      {awsWellArchitectPrinciplesLink}
      <span>.</span>
    </>,
  ];

  const contentTypeItemMap = new Map<ContentType, Array<string | JSX.Element>>([
    [ContentType.Strategy, strategyItems],
    [ContentType.Guide, guideItems],
    [ContentType.Pattern, patternItems],
  ]);

  const openContentItems = (contentTypeItemMap.get(contentType) || []).filter((item) => !!item);

  const contentChecklistInfo: ChecklistInfo = new ChecklistInfo(
    constants.OPEN_CONTENT_ITEMS_TEXT,
    openContentItems,
    constants.OPEN_CONTENT_ITEMS_INSTRUCTION,
  );

  const itemsChecklist = (
    <>
      <div>
        <span>Please review below checklist and APG content guidelines </span>
        {reviewerBrReviewGuideLink}
        <span> before approving content.</span>
      </div>
      <ChecklistGroup
        contentId={contentId}
        listName={reviewerChecklistListName}
        onAllChecked={setAllChecked}
        checklistInfo={[contentChecklistInfo]}
        workflowId={workflowId}
      />
    </>
  );

  const resetChecklistState = () => {
    LocalStorage.resetChecklistState(contentId, reviewerChecklistListName);
  };

  const handleReviewComplete = () => {
    resetChecklistState();
    if (onReviewComplete) {
      onReviewComplete();
    }
  };

  const reviewControls = (
    <ReviewChecklistControls
      contentId={contentId}
      disabled={!allChecked}
      onReviewComplete={handleReviewComplete}
      workflowId={workflowId}
      workflow={workflow}
      approvalDisabled={approvalDisabled}
      disableApproveMessage={disableApproveMessage}
    />
  );

  return (
    <div id="reviewer-checklist">
      <Box margin={{ top: 's' }}>
        <Container header={<Header variant="h3">{constants.CONTENT_CHECKLIST_HEADER}</Header>}>
          {itemsChecklist}
          {reviewControls}
        </Container>
      </Box>
    </div>
  );
};

export default ReviewerChecklist;
